/**
 * NEO → Goal Game. The owner's own back end opens the SAME Game Results and
 * Live Game View the portal shows, read through the five-minute NEO session
 * instead of a portal login. The server decides the scope from that signed
 * token, so this page passes nothing but the token.
 *
 * Session: the board polls every ten seconds, so the token is renewed while
 * the screen is actually being watched — same session, same claims, a fresh
 * window of the SAME five minutes, never longer. An abandoned phone simply
 * stops renewing and the session ends, which is said plainly on screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import GameResults from './GameResults';
import LiveGameView from './LiveGameView';

const API_BASE = window.location.origin;
const RENEW_EVERY_MS = 3 * 60 * 1000;     // inside the five-minute window
const ACTIVE_WITHIN_MS = 2 * 60 * 1000;   // "still being watched"

export default function ManagerGameView({ view }) {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [token, setToken] = useState(null);
    const [ended, setEnded] = useState(false);
    const [ready, setReady] = useState(false);
    const lastTouch = useRef(Date.now());

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(`mgr_session_${cardId}`);
            const saved = raw ? JSON.parse(raw) : null;
            if (saved?.session_token && saved?.scope?.goal_game_enabled) {
                setToken(saved.session_token);
            } else setEnded(true);
        } catch { setEnded(true); }
        setReady(true);
    }, [cardId]);

    // Any touch, scroll or key counts as "watching".
    useEffect(() => {
        const touch = () => { lastTouch.current = Date.now(); };
        ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(
            (e) => window.addEventListener(e, touch, { passive: true }));
        return () => ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(
            (e) => window.removeEventListener(e, touch));
    }, []);

    const endSession = useCallback(() => {
        sessionStorage.removeItem(`mgr_session_${cardId}`);
        setToken(null);
        setEnded(true);
    }, [cardId]);

    useEffect(() => {
        if (!token || ended) return undefined;
        const id = setInterval(async () => {
            const watching = document.visibilityState === 'visible'
                && Date.now() - lastTouch.current < ACTIVE_WITHIN_MS;
            if (!watching) return;
            try {
                const res = await fetch(`${API_BASE}/api/manager/session/renew`, {
                    method: 'POST',
                    headers: { 'X-Manager-Session': token, 'Content-Type': 'application/json' },
                });
                if (!res.ok) { endSession(); return; }
                const data = await res.json();
                setToken(data.session_token);
                const raw = sessionStorage.getItem(`mgr_session_${cardId}`);
                const saved = raw ? JSON.parse(raw) : {};
                sessionStorage.setItem(`mgr_session_${cardId}`, JSON.stringify({
                    ...saved, session_token: data.session_token,
                    exp: Date.now() + (data.expires_in - 2) * 1000,
                }));
            } catch { /* one failed renewal is not the end — the next poll decides */ }
        }, RENEW_EVERY_MS);
        return () => clearInterval(id);
    }, [token, ended, cardId, endSession]);

    if (!ready) {
        return <div className="min-h-screen bg-slate-950 p-8" data-testid="neo-game-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
        </div>;
    }

    if (ended || !token) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6"
                 data-testid="neo-session-ended">
                <div className="text-center space-y-4 max-w-sm">
                    <KeyRound className="w-10 h-10 text-fuchsia-400 mx-auto" />
                    <p className="text-lg font-semibold text-white">
                        {t('neoGame.sessionEnded', 'Session ended, enter your code again')}
                    </p>
                    <Button onClick={() => navigate(`/manager/${cardId}`)}
                            className="min-h-[48px] w-full bg-fuchsia-600 hover:bg-fuchsia-700"
                            data-testid="neo-session-ended-btn">
                        {t('neoGame.signInAgain', 'Back to NEO')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950" data-testid={`neo-game-${view}`}>
            <div className="sticky top-0 z-30 flex items-center gap-2 border-b border-slate-800
                            bg-slate-950/95 px-3 py-2 backdrop-blur">
                <Button variant="ghost" onClick={() => navigate(`/manager/${cardId}`)}
                        className="min-h-[44px] text-slate-300 hover:text-white"
                        data-testid="neo-game-back">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('neoGame.back', 'NEO')}
                </Button>
                <span className="text-sm font-semibold text-white">
                    {view === 'live'
                        ? t('neoGame.liveTitle', 'Live Game View')
                        : t('neoGame.resultsTitle', 'Game Results')}
                </span>
            </div>
            {view === 'live'
                ? <LiveGameView managerToken={token} onSessionEnded={endSession} />
                : <GameResults managerToken={token} onSessionEnded={endSession} />}
        </div>
    );
}
